import { browserLoginScope } from '@/lib/auth/browser-login-scope'
import {
  isDeletedAccount,
  subscribeDeletedAccounts,
} from '@/lib/auth/deleted-account-state'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'

import type { SessionSnapshot } from '@scrambl/shared/session-scope'
import {
  createSessionScope,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

/**
 * Cross-tab identity broadcast. Cookies are shared by every tab of a browser
 * profile, but each tab's auth store is not: a tab left open across a
 * sign-out/sign-in in another tab keeps the old user in memory while its
 * requests already carry the new user's cookie. Its next fetch would return
 * the new account's data and its persister would file it under the old
 * account's cache bucket (and the old account's rows would linger in memory
 * until the tab's own auth check ran). Writing the signed-in user id here
 * fires `storage` in every *other* tab, which then drops its session the same
 * way the desktop app does on a native account switch below.
 */
export const SESSION_USER_STORAGE_KEY = 'scrambl-session-user'
// Starts as null so a booting tab (user not resolved yet) never announces a
// sign-out; only a real signed-in -> signed-out transition is broadcast.
let lastBroadcastUserId: string | null = null
let isApplyingRemoteIdentity = false

function broadcastSessionUser(userId: string | null) {
  if (isApplyingRemoteIdentity || userId === lastBroadcastUserId) return
  lastBroadcastUserId = userId
  try {
    window.localStorage.setItem(SESSION_USER_STORAGE_KEY, userId ?? '')
  } catch {
    // Storage unavailable: other tabs fall back to their own auth checks.
  }
}

/**
 * The menu sign-out (utils/auth/auth-utils) replaces the page without ever
 * nulling the store, so it has to announce itself; the equality short-cut
 * above would otherwise swallow it in a tab whose user never resolved.
 */
export function announceSignOut() {
  lastBroadcastUserId = null
  try {
    window.localStorage.setItem(SESSION_USER_STORAGE_KEY, '')
  } catch {
    // Storage unavailable: other tabs fall back to their own auth checks.
  }
}

const scope = createSessionScope()
const identityScope = createSessionScope()
let observedDesktopAccount: string | null = null
let hasDesktopProfile = false
let observedPrincipal: string | null = null
function refreshScope() {
  const deletedUser = useAuth.getState().user?.id
  if (deletedUser && isDeletedAccount(deletedUser)) {
    const previous = isApplyingRemoteIdentity
    isApplyingRemoteIdentity = true
    lastBroadcastUserId = null
    try {
      useAuth.setState({
        user: null,
        isAuthenticated: false,
        isAuthReady: true,
        lastAuthCheckTime: null,
      })
      useOrganizationStore.getState().setCurrentOrganization?.(null)
      refreshScope()
    } finally {
      isApplyingRemoteIdentity = previous
    }
    return
  }
  const browserLogin = browserLoginScope.capture()
  const userId = useAuth.getState().user?.id ?? null
  broadcastSessionUser(userId)
  const org = useOrganizationStore.getState()
  const desktop = desktopAccountSession.capture()
  const desktopAccountId = desktop.identity
  // The native vault identifies a companion window before its profile loads.
  // Hydrating that same profile must not invalidate its own startup requests.
  if (desktopAccountId !== observedDesktopAccount) {
    observedDesktopAccount = desktopAccountId
    hasDesktopProfile = false
  }
  if (userId && userId === desktopAccountId) hasDesktopProfile = true
  // Once a profile has loaded, clearing it is sign-out, not another bootstrap.
  const candidate = userId ?? (hasDesktopProfile ? null : desktopAccountId)
  const principalId =
    candidate && !isDeletedAccount(candidate) ? candidate : null
  observedPrincipal = principalId
  const browserRevision = desktopAccountId ? null : browserLogin.identity
  identityScope.transition(
    principalId || browserRevision
      ? JSON.stringify([
          principalId,
          desktopAccountId,
          desktop.generation,
          browserRevision,
        ])
      : null,
  )
  scope.transition(
    principalId || browserRevision
      ? JSON.stringify([
          principalId,
          desktopAccountId,
          desktop.generation,
          browserRevision,
          org.currentOrganization?.id,
          !!org.currentOrganization?.isExternalAccess,
          org.externalOrgRelationship?.id,
        ])
      : null,
  )
}
useAuth.subscribe?.(refreshScope)
subscribeDeletedAccounts(refreshScope)
useOrganizationStore.subscribe?.(refreshScope)
function captureBoundScope(source: typeof scope) {
  const captured = source.capture()
  const desktop = desktopAccountSession.capture()
  const browser = desktop.identity ? null : browserLoginScope.capture()
  const actor = useAuth.getState().user?.id ?? desktop.identity
  return {
    ...captured,
    // A revision invalidates even anonymous bootstrap work; it grants no identity.
    identity: observedPrincipal ? captured.identity : null,
    actorId: useAuth.getState().user?.id ?? desktop.identity,
    profileId: useAuth.getState().user?.id ?? null,
    assertCurrent() {
      if (actor && isDeletedAccount(actor)) throw new SessionChangedError()
      captured.assertCurrent()
      desktop.assertCurrent()
      browser?.assertCurrent()
      const desktopAccountId = desktopAccountSession.capture().identity
      const userId = useAuth.getState().user?.id
      if (desktopAccountId && userId && desktopAccountId !== userId) {
        throw new SessionChangedError()
      }
    },
  }
}
/** Observe ownership without adding requests or a second identity authority. */
export const subscribeRequestScope = (listener: () => void) =>
  scope.subscribe(listener)
export function readRequestScopeGeneration(): number {
  refreshScope()
  return scope.capture().generation
}

export function captureRequestScope() {
  refreshScope()
  return captureBoundScope(scope)
}
export const subscribeIdentityScope = (listener: () => void) =>
  identityScope.subscribe(listener)
export function readIdentityScopeGeneration(): number {
  refreshScope()
  return identityScope.capture().generation
}

export function captureIdentityScope() {
  refreshScope()
  return captureBoundScope(identityScope)
}

function isAuthPage(pathname: string) {
  return /^\/(login|register|auth)(\/|$)/.test(pathname)
}

browserLoginScope.subscribe(remote => {
  if (desktopAccountSession.capture().identity) return
  const hadProfile = !!useAuth.getState().user
  isApplyingRemoteIdentity = true
  try {
    useAuth.setState({
      user: null,
      isAuthenticated: false,
      isAuthReady: false,
      lastAuthCheckTime: null,
    })
    useOrganizationStore.getState().setCurrentOrganization?.(null)
    refreshScope()
  } finally {
    isApplyingRemoteIdentity = false
  }
  if (remote && hadProfile && !isAuthPage(window.location.pathname))
    window.location.replace('/organizations')
})

/** Another tab signed out, or signed in as someone else. */
export function handleRemoteSessionUser(nextUserId: string | null) {
  const mine = useAuth.getState().user?.id ?? null
  // Remember what the other tab announced so this tab's own store update
  // below is not re-broadcast as a second change.
  lastBroadcastUserId = nextUserId
  if (!mine || nextUserId === mine) return
  isApplyingRemoteIdentity = true
  try {
    useAuth.setState({ user: null, isAuthenticated: false })
    useOrganizationStore.getState().setCurrentOrganization?.(null)
    refreshScope()
  } finally {
    isApplyingRemoteIdentity = false
  }
  if (!isAuthPage(window.location.pathname)) {
    window.location.replace('/organizations')
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    // `key === null` is `localStorage.clear()`: the purge sign-out after an
    // account deletion, which wipes every local record including this one.
    if (event.key !== null && event.key !== SESSION_USER_STORAGE_KEY) return
    try {
      handleRemoteSessionUser(
        window.localStorage.getItem(SESSION_USER_STORAGE_KEY) || null,
      )
    } catch {
      // Storage unavailable: each tab must settle its own profile check.
    }
  })
}

const localLogoutTransitions = new Set<SessionSnapshot>()

/** The initiating window finishes its own logout; other windows still reload. */
export function registerLocalLogoutTransition(session: SessionSnapshot) {
  session.assertCurrent()
  localLogoutTransitions.add(session)
  return () => {
    localLogoutTransitions.delete(session)
  }
}

let previousDesktopAccount = desktopAccountSession.capture().identity
desktopAccountSession.subscribe(() => {
  const next = desktopAccountSession.capture().identity
  const auth = useAuth.getState()
  const native = desktopAccountSession.capture()
  const completingLocalLogout =
    next === null &&
    [...localLogoutTransitions].some(
      session =>
        session.identity === previousDesktopAccount &&
        native.generation === session.generation + 1,
    )
  const changedUser =
    previousDesktopAccount !== null &&
    auth.user &&
    (previousDesktopAccount === next || auth.user.id !== next)
  previousDesktopAccount = next
  // This subscription fires only when the native account or login epoch changes.
  // A same-account login also needs a fresh profile and membership resolution.
  // Canonical-ID hydration already matching the verified profile may continue.
  refreshScope()
  if (changedUser) {
    useAuth.setState({ user: null, isAuthenticated: false })
    useOrganizationStore.getState().setCurrentOrganization?.(null)
    if (!completingLocalLogout && !isAuthPage(window.location.pathname)) {
      window.location.replace('/organizations')
    }
  }
})
